// views/quiz.tsx — Marketing → Quiz.
//
// Two halves, one screen:
//   • ANALYTICS — funnel, per-question drop-off, A/B intro test, per-product rec
//     performance, and quiz-attributed revenue. Reads /api/quiz/analytics.
//   • CONTENT — the versioned quiz definition the storefront fetches: intro
//     variants, questions/options/scores, products & colors. Edits save as a
//     draft; Publish makes it live. Reads/writes /api/quiz/config.
//
// The storefront (Shopify) ALSO bakes a default copy of this content, so "no
// published version yet" is a valid, non-breaking state — that's the empty state
// the Content tab explains. Backend: worker/quiz.ts.

import React from "react";
import { OSWALD, FIGTREE, useT, useIsMobile } from "../theme";
import { Icon, Card, Badge, Btn, Eyebrow } from "../atoms";
import { PageHead, StatTile } from "./finance";
import { QuizBuilder } from "./quizbuilder";
import { STARTER } from "./quizcontent";

const inputStyle = (t: any): React.CSSProperties => ({ fontFamily: FIGTREE, fontSize: 13, padding: "8px 10px", borderRadius: 8, border: "1px solid " + t.border, background: t.surface2, color: t.fg, width: "100%", boxSizing: "border-box" });

// ------------------------------------------------------------------ shell ----
export function QuizView() {
  const isMobile = useIsMobile();
  const [tab, setTab] = React.useState<"analytics" | "content" | "leads">("analytics");
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <PageHead isMobile={isMobile} title="Quiz"
        sub="The Detox Finder quiz on the storefront — how it performs, what it says, and who took it."
        right={<Segmented value={tab} onChange={setTab as any} options={[{ id: "analytics", label: "Analytics" }, { id: "content", label: "Content" }, { id: "leads", label: "Leads" }]} />}
      />
      {tab === "analytics" ? <QuizAnalytics /> : tab === "content" ? <QuizContent /> : <QuizLeads />}
    </div>
  );
}

// ------------------------------------------------------------------ leads ----
// Support surface for "I took the quiz and never got my results" — nearly always
// a typo'd email. Find the person, fix the address, re-send. Correcting the email
// re-queues the lead, which re-fires the Klaviyo event and re-sends the results.
function QuizLeads() {
  const t = useT();
  const [q, setQ] = React.useState("");
  const [leads, setLeads] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [retention, setRetention] = React.useState<number | null>(null);
  const [editing, setEditing] = React.useState<number | null>(null);
  const [newEmail, setNewEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  const load = React.useCallback((query: string) => {
    setLoading(true);
    fetch(`/api/quiz/leads${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setLeads(d?.leads ?? []); setRetention(d?.retentionDays ?? null); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(""); }, [load]);

  async function resend(id: number, email?: string) {
    setBusy(true); setMsg(null);
    const r = await fetch(`/api/quiz/leads/${id}/resend`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(email ? { email } : {}),
    }).catch(() => null);
    setBusy(false);
    if (r?.ok) { setMsg(email ? `Corrected and re-sent to ${email}.` : "Re-sent."); setEditing(null); setNewEmail(""); load(q); }
    else setMsg("Failed — check the email address is valid.");
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card>
        <PanelHead icon="search" title="Find a quiz taker"
          note="Search by email or first name — partial matches work, so a typo'd address is still findable. Blank shows the most recent." />
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <input value={q} placeholder="email, name, or leave blank for recent"
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)}
            style={{ ...inputStyle(t), flex: "1 1 260px", width: "auto" }} />
          <Btn variant="solid" tone="dark" icon="search" onClick={() => load(q)}>Search</Btn>
        </div>
        {msg && <div style={{ marginTop: 10, fontFamily: FIGTREE, fontSize: 12.5, color: t.good }}>{msg}</div>}
        {retention != null && (
          <div style={{ marginTop: 10, fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>
            Lead contact details are kept {retention} days, then deleted. Quiz analytics are anonymous and kept indefinitely.
          </div>
        )}
      </Card>

      {loading ? (
        <Card><div style={{ padding: 20, fontFamily: FIGTREE, color: t.muted }}>Loading…</div></Card>
      ) : leads.length === 0 ? (
        <Card><Empty>No leads found. Email capture is optional on the results page, so someone can finish the quiz without leaving an address.</Empty></Card>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {leads.map((l) => {
            const open = editing === l.id;
            const resent = fmtResent(l.resentAt);
            return (
            <Card key={l.id} pad={20}>
              {/* Top row: name/meta + actions. Wraps so the buttons drop below the
                  name on a phone (README mobile stacking). */}
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ minWidth: 0, flex: "1 1 260px" }}>
                  <div style={{ fontFamily: FIGTREE, fontSize: 15, color: t.fg }}>
                    <b style={{ fontWeight: 700 }}>{l.firstName || "—"}</b> <span style={{ color: t.faint }}>·</span> <span style={{ color: t.purple }}>{l.email}</span>
                  </div>
                  <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                    {l.createdAt} · {l.topArea ? <>top match <b style={{ color: t.fg2, textTransform: "capitalize" }}>{l.topArea}</b></> : "no result recorded"}
                    {l.recommended?.length > 0 && <> · recs: {l.recommended.map((r: any) => (typeof r === "string" ? r : r.pid)).join(", ")}</>}
                  </div>
                  {l.syncError && <div style={{ marginTop: 6, fontFamily: FIGTREE, fontSize: 11.5, color: t.bad }}>{l.syncError}</div>}
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0, flexWrap: "wrap" }}>
                  <Btn size="sm" variant={open ? "soft" : "outline"} tone={open ? "accent" : "dark"} icon="doc" disabled={busy}
                    onClick={() => { setEditing(open ? null : l.id); setNewEmail(l.email); setMsg(null); }}>Fix email</Btn>
                  <Btn size="sm" variant="soft" tone="accent" icon="send" disabled={busy} onClick={() => resend(l.id)}>Re-send</Btn>
                </div>
              </div>

              {/* Pill row: status + consent, with the resend audit marker pushed to
                  the far right (wraps to its own line on narrow widths). */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
                {l.syncedAt ? <Badge tone="good">Sent {l.syncedAt}</Badge> : l.syncError ? <Badge tone="bad">Failed</Badge> : <Badge tone="warn">Queued</Badge>}
                <Badge tone={l.consent ? "neutral" : "warn"}>{l.consent ? "Marketing consent" : "No marketing consent"}</Badge>
                {resent && <span style={{ marginLeft: "auto", fontFamily: FIGTREE, fontSize: 12.5, color: t.good, fontVariantNumeric: "tabular-nums" }}>Resent · {resent}</span>}
              </div>

              {/* FIX EMAIL editor — slides open. Corrected address is saved here,
                  pushed to Klaviyo, and the results email re-sent. */}
              {open && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid " + t.border }}>
                  <Eyebrow style={{ color: t.purple, marginBottom: 8 }}>Correct the email address</Eyebrow>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="corrected@email.com"
                      onKeyDown={(e) => e.key === "Enter" && newEmail.trim() && resend(l.id, newEmail.trim())}
                      style={{ ...inputStyle(t), flex: "1 1 240px", width: "auto", border: "1px solid " + t.accentSoft }} />
                    <Btn size="sm" variant="outline" disabled={busy} onClick={() => setEditing(null)}>Cancel</Btn>
                    <Btn size="sm" variant="solid" tone="accent" icon="check" disabled={busy || !newEmail.trim()} onClick={() => resend(l.id, newEmail.trim())}>
                      {busy ? "Sending…" : "Save, sync & re-send"}
                    </Btn>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12 }}>
                    <Icon name="alert" size={14} color={t.muted} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, lineHeight: 1.55 }}>
                      Saving updates the lead here <b style={{ color: t.fg2 }}>and pushes the corrected address to Klaviyo</b> so the profile matches, then re-sends their results. Only use this when a customer gives you an updated email.
                    </span>
                  </div>
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Segmented({ value, onChange, options }: any) {
  const t = useT();
  return (
    <div style={{ display: "inline-flex", background: t.surface2, border: "1px solid " + t.border, borderRadius: 999, padding: 3, gap: 2 }}>
      {options.map((o: any) => {
        const active = o.id === value;
        return (
          <button key={o.id} onClick={() => onChange(o.id)} style={{
            fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase",
            padding: "6px 14px", borderRadius: 999, border: "none", cursor: "pointer",
            background: active ? t.fg : "transparent", color: active ? t.surface : t.muted, transition: "background 140ms",
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

const pct = (v: number) => (v == null ? "—" : Math.round(v * 100) + "%");
const money = (v: number, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(v || 0);

// "Resent · Jul 24, 2026 4:38 PM" — the resend audit marker. resent_at is stored
// as a naive UTC datetime (SQLite datetime('now')); render it in the store's
// timezone so it lines up with the rest of the page's Mountain-time stamps.
function fmtResent(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Denver", month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  }).formatToParts(d).reduce((a: any, p) => { a[p.type] = p.value; return a; }, {});
  return `${parts.month} ${parts.day}, ${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
}

// -------------------------------------------------------------- analytics ----
function QuizAnalytics() {
  const t = useT();
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [syncing, setSyncing] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    fetch(`/api/quiz/analytics?days=${days}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  if (loading && !data) return <Card><div style={{ padding: 24, fontFamily: FIGTREE, color: t.muted }}>Loading…</div></Card>;

  const f = data?.funnel || {};
  const noData = !data?.hasData;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Segmented value={String(days)} onChange={(v: string) => setDays(parseInt(v, 10))}
          options={[{ id: "7", label: "7d" }, { id: "30", label: "30d" }, { id: "90", label: "90d" }]} />
      </div>

      {noData && (
        <Card style={{ borderStyle: "dashed" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: 4 }}>
            <span style={{ width: 40, height: 40, borderRadius: 10, background: t.claudeSoft, display: "grid", placeItems: "center", flexShrink: 0 }}>
              <Icon name="quiz" size={20} color={t.claude} />
            </span>
            <div>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 16, textTransform: "uppercase", letterSpacing: "0.02em", color: t.fg }}>No quiz data yet</div>
              <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5, lineHeight: 1.55, maxWidth: 620 }}>
                These numbers populate once the Detox Finder quiz is live on the storefront and reporting events to the
                dashboard. The funnel, drop-off, A/B and attribution views below are wired and will fill in automatically —
                nothing else to do here until the theme build ships.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Funnel KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14 }}>
        <StatTile label="Quiz starts" value={f.started ?? 0} icon="pulse" />
        <StatTile label="Completion rate" value={pct(f.completionRate)} sub={`${f.completed ?? 0} completed`} accent={t.good} icon="check" />
        <StatTile label="Add-to-cart rate" value={pct(f.addToCartRate)} sub="of completions" icon="cart" />
        <StatTile label="Email capture" value={pct(f.emailRate)} sub="of completions" icon="mail" />
      </div>

      {/* Drop-off per question */}
      <Card>
        <PanelHead icon="alert" title="Drop-off by question" note="Where unfinished sessions stopped — % of those who reached a question and left there." />

        {/* Intro bounce sits above the questions because it is a different leak:
            these people never reached question 1, so no per-question row can ever
            account for them. Shown with raw counts, not just a rate — 60% of 5 and
            60% of 500 are not the same finding, and a bare percentage hides which
            one you're looking at. */}
        {data?.introBounce && (
          <div style={{
            display: "grid", gridTemplateColumns: "minmax(120px,1.4fr) 3fr auto", gap: 12,
            alignItems: "center", padding: "10px 12px", marginTop: 6, marginBottom: 4,
            background: t.surface2, borderRadius: 10,
            border: "1px solid " + (data.introBounce.rate > 0.4 ? t.bad : "transparent"),
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: FIGTREE, fontSize: 13, fontWeight: 700, color: t.fg }}>
                Left at the intro
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>
                Landed and never reached question 1
              </div>
            </div>
            <div style={{ height: 10, borderRadius: 999, background: t.surface, overflow: "hidden" }}>
              <div style={{
                width: Math.min(100, (data.introBounce.rate || 0) * 100) + "%", height: "100%",
                background: data.introBounce.rate > 0.4 ? t.bad : t.accent, borderRadius: 999, transition: "width .4s",
              }} />
            </div>
            <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 13, color: t.fg, minWidth: 132, textAlign: "right" }}>
              {pct(data.introBounce.rate)}{" "}
              <span style={{ color: t.faint, fontWeight: 500, fontSize: 12 }}>
                · {data.introBounce.bounced} of {data.introBounce.started} starts
              </span>
            </span>
          </div>
        )}

        {(!data?.dropoff || data.dropoff.length === 0) ? (
          <Empty>No question-level data in this range.</Empty>
        ) : (
          <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
            {data.dropoff.map((d: any) => (
              <div key={d.qid} style={{ display: "grid", gridTemplateColumns: "minmax(120px,1.4fr) 3fr auto", gap: 12, alignItems: "center" }}>
                <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.label}</span>
                <div style={{ height: 10, borderRadius: 999, background: t.surface2, overflow: "hidden" }}>
                  <div style={{ width: Math.min(100, d.dropRate * 100) + "%", height: "100%", background: d.dropRate > 0.25 ? t.bad : t.accent, borderRadius: 999, transition: "width .4s" }} />
                </div>
                <span style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.muted, minWidth: 84, textAlign: "right" }}>
                  {pct(d.dropRate)} <span style={{ color: t.faint, fontWeight: 500 }}>· {d.leftHere}/{d.reached}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16 }}>
        {/* A/B intro variants */}
        <Card>
          <PanelHead icon="spark" title="Intro A/B test" note="Completion rate by intro title variant." />
          {(!data?.variants || data.variants.length === 0) ? <Empty>No A/B data yet.</Empty> : (
            <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
              {data.variants.map((v: any) => (
                <div key={v.variant} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: t.surface2, borderRadius: 10 }}>
                  <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>Variant {v.variant}</span>
                  <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>
                    <b style={{ color: t.fg }}>{pct(v.completionRate)}</b> · {v.completed}/{v.started}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Top areas */}
        <Card>
          <PanelHead icon="health" title="Top detox match" note="Winning area across completed quizzes." />
          {(!data?.topAreas || data.topAreas.length === 0) ? <Empty>No completions yet.</Empty> : (
            <div style={{ display: "grid", gap: 8, marginTop: 6 }}>
              {data.topAreas.map((a: any) => (
                <div key={a.area} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", background: t.surface2, borderRadius: 10 }}>
                  <span style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, textTransform: "capitalize" }}>{a.area}</span>
                  <b style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg }}>{a.count}</b>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Per-product recommendation performance */}
      <Card>
        <PanelHead icon="box" title="Recommended product performance" note="For products shown on the results page: how often each was clicked through to its PDP, added to cart, and what it earned." />
        {(!data?.products || data.products.length === 0) ? <Empty>No recommendations shown in this range.</Empty> : (
          <div style={{ overflowX: "auto", marginTop: 6 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FIGTREE, fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: t.muted }}>
                  {["Product", "Shown", "PDP clicks", "Added", "PDP CTR", "Add rate", "Orders", "Revenue"].map((h, i) => (
                    <th key={h} style={{ padding: "6px 8px", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 0 ? "left" : "right" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.products.map((p: any) => (
                  <tr key={p.pid} style={{ borderTop: "1px solid " + t.border }}>
                    <td style={{ padding: "8px", color: t.fg, fontWeight: 600 }}>{p.name}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: t.fg2 }}>{p.shown}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: t.fg2 }}>{p.viewed}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: t.fg2 }}>{p.added}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: t.fg }}>{pct(p.pdpCtr)}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: t.fg }}>{pct(p.addRate)}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: t.fg2 }}>{p.orders ?? 0}</td>
                    <td style={{ padding: "8px", textAlign: "right", color: (p.revenue ?? 0) > 0 ? t.good : t.faint, fontWeight: 700 }}>
                      {money(p.revenue ?? 0, data?.attribution?.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 10, lineHeight: 1.5 }}>
              <b style={{ color: t.muted }}>Shown / PDP clicks / Added</b> count sessions that started in this range.
              <b style={{ color: t.muted }}> Orders / Revenue</b> count orders that landed in this range, so a quiz taken
              late in the window and bought after it won’t line up between the two halves — read them as directional.
              Revenue is per product from the tagged order lines; the reconciling total is “Attributed revenue” above.
            </div>
          </div>
        )}
      </Card>

      {/* Email leads → Klaviyo */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <PanelHead icon="mail" title="Email leads → Klaviyo" note="Captured on the results page, queued, and synced by cron every 15 min." />
          <Btn size="sm" variant="outline" icon="refresh" disabled={syncing}
            onClick={async () => {
              setSyncing(true);
              await fetch("/api/quiz/leads/sync", { method: "POST" }).catch(() => {});
              const d = await fetch(`/api/quiz/analytics?days=${days}`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
              if (d) setData(d);
              setSyncing(false);
            }}>{syncing ? "Syncing…" : "Sync now"}</Btn>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginTop: 6 }}>
          <MiniStat label="Leads captured" value={data?.leads?.total ?? 0} />
          <MiniStat label="Synced" value={data?.leads?.synced ?? 0} accent={t.good} />
          <MiniStat label="Pending" value={data?.leads?.pending ?? 0} />
          <MiniStat label="Marketing consent" value={data?.leads?.consented ?? 0} />
        </div>
        {data?.leads?.parked > 0 && (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: t.badSoft, fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2 }}>
            <b style={{ color: t.bad }}>{data.leads.parked} lead{data.leads.parked === 1 ? "" : "s"} failed to sync</b> after 5 attempts and are parked.
            {data.leads.lastError && <> Last error: <code style={{ fontSize: 11.5 }}>{data.leads.lastError}</code></>}
            {" "}Fix the cause, then hit <b>Sync now</b>.
          </div>
        )}
      </Card>

      {/* Attribution */}
      <Card>
        <PanelHead icon="dollar" title="Quiz-attributed revenue" note="Purchases tied back to a quiz session via cart attributes (populated by the order webhook)." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginTop: 6 }}>
          <MiniStat label="Attributed orders" value={data?.attribution?.orders ?? 0} />
          <MiniStat label="Attributed revenue" value={money(data?.attribution?.revenue ?? 0, data?.attribution?.currency)} accent={t.good} />
        </div>
        {data?.attribution?.perProduct?.length > 0 && (
          <div style={{ display: "grid", gap: 6, marginTop: 12 }}>
            {data.attribution.perProduct.map((p: any) => (
              <div key={p.pid} style={{ display: "flex", justifyContent: "space-between", padding: "7px 10px", background: t.surface2, borderRadius: 10, fontFamily: FIGTREE, fontSize: 13 }}>
                <span style={{ color: t.fg2, textTransform: "capitalize" }}>{p.pid}</span>
                <span style={{ color: t.muted }}>{p.orders} orders · <b style={{ color: t.fg }}>{money(p.revenue, data.attribution.currency)}</b></span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function PanelHead({ icon, title, note }: any) {
  const t = useT();
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon name={icon} size={16} color={t.accent} />
        <span style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.03em", color: t.fg }}>{title}</span>
      </div>
      {note && <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 3 }}>{note}</div>}
    </div>
  );
}
const Empty = ({ children }: any) => { const t = useT(); return <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.faint, padding: "14px 4px" }}>{children}</div>; };
function MiniStat({ label, value, accent }: any) {
  const t = useT();
  return (
    <div style={{ background: t.surface2, borderRadius: 12, padding: "12px 14px" }}>
      <Eyebrow>{label}</Eyebrow>
      <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 26, color: accent || t.fg, marginTop: 4 }}>{value}</div>
    </div>
  );
}

// A doc saved before a `ui` key existed simply has no value for it, so the
// builder would render an empty box and the copy editor would have no idea what
// the storefront is actually saying. Backfill anything missing from STARTER,
// which holds the storefront's own defaults — so a newly exposed string shows up
// pre-filled with the live wording instead of blank.
//
// Only ever ADDS absent keys: an edited value is never overwritten, and the doc
// is left untouched (not marked dirty) when there's nothing to add.
function seedNewUiKeys(doc: any): any {
  if (!doc || typeof doc !== "object") return doc;
  const starter = (STARTER as any).ui || {};
  const current = doc.ui && typeof doc.ui === "object" ? doc.ui : {};
  const missing: Record<string, string> = {};
  for (const k of Object.keys(starter)) {
    if (typeof current[k] !== "string") missing[k] = starter[k];
  }
  if (!Object.keys(missing).length) return doc;
  return { ...doc, ui: { ...missing, ...current } };
}

// ---------------------------------------------------------------- content ----
function QuizContent() {
  const t = useT();
  const [state, setState] = React.useState<any>({ loading: true, published: null, draft: null, versions: [] });
  const [doc, setDoc] = React.useState<any>(null); // working copy
  const [dirty, setDirty] = React.useState(false);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [msg, setMsg] = React.useState<string | null>(null);
  const [baseVersion, setBaseVersion] = React.useState<number | null>(null);
  // Set when the Worker rejects a save as stale. Blocks further saving until the
  // editor reloads, so the second attempt can't just win by trying again.
  const [conflict, setConflict] = React.useState<string | null>(null);

  const load = React.useCallback(() => {
    fetch("/api/quiz/config").then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!d) { setState({ loading: false, published: null, draft: null, versions: [] }); return; }
      setState({ loading: false, ...d });
      setDoc(seedNewUiKeys(d.draft?.doc || d.published?.doc || null));
      // The newest version this tab has seen. Sent with every save so the Worker
      // can refuse if the config moved on in the meantime — a stale tab silently
      // overwriting newer edits is how 27 copy fields got reverted once already.
      setBaseVersion(Math.max(d.draft?.version ?? 0, d.published?.version ?? 0,
        ...(d.versions ?? []).map((v: any) => v.version ?? 0)));
      setDirty(false);
    }).catch(() => setState({ loading: false, published: null, draft: null, versions: [] }));
  }, []);
  React.useEffect(load, [load]);

  const update = (fn: (d: any) => void) => setDoc((prev: any) => { const n = structuredClone(prev || {}); fn(n); return n; }) as any;
  const touch = (fn: (d: any) => void) => { update(fn); setDirty(true); setMsg(null); };

  // Returns true if the response was a stale-edit conflict, after recording it.
  async function handledConflict(r: Response | null): Promise<boolean> {
    if (!r || r.status !== 409) return false;
    const d = await r.json().catch(() => null as any);
    setConflict(d?.detail || "This tab is out of date. Reload before saving.");
    setMsg(null);
    return true;
  }

  async function saveDraft() {
    if (conflict) return;
    setBusy("save"); setMsg(null);
    const r = await fetch("/api/quiz/config/draft", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ doc, baseVersion }),
    }).catch(() => null);
    setBusy(null);
    if (await handledConflict(r)) return;
    if (r?.ok) { setDirty(false); setMsg("Draft saved."); load(); } else { setMsg("Save failed."); }
  }
  async function publish() {
    if (conflict) return;
    setBusy("publish"); setMsg(null);
    if (dirty) {
      const r0 = await fetch("/api/quiz/config/draft", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ doc, baseVersion }),
      }).catch(() => null);
      if (await handledConflict(r0)) { setBusy(null); return; }
      if (!r0?.ok) { setBusy(null); setMsg("Save failed."); return; }
    }
    const r = await fetch("/api/quiz/config/publish", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({}) }).catch(() => null);
    // The Worker refuses configs the storefront would reject; show its reason
    // rather than a generic failure, since the reason is always actionable.
    if (r && !r.ok) {
      const detail = await r.json().then((d: any) => d?.detail).catch(() => null);
      setBusy(null); setMsg(detail || "Publish failed.");
      return;
    }
    setBusy(null);
    if (r?.ok) { setDirty(false); setMsg("Published live."); load(); } else { setMsg("Publish failed."); }
  }

  if (state.loading) return <Card><div style={{ padding: 24, fontFamily: FIGTREE, color: t.muted }}>Loading…</div></Card>;

  const liveVer = state.published?.version;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Stale-edit conflict. Deliberately loud and blocking: the failure mode it
          prevents is silent, and the last time it happened it reverted 27 fields
          of hand-written copy to defaults. Saving stays disabled until reload. */}
      {conflict && (
        <Card style={{ border: "1px solid " + t.bad, background: "rgba(220,80,60,0.06)" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.05em", color: t.bad }}>
                Not saved — this tab is out of date
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, lineHeight: 1.55, marginTop: 6, maxWidth: 640 }}>
                {conflict}
              </div>
              <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 6 }}>
                Your unsaved edits in this tab will be lost on reload — copy anything you need first.
              </div>
            </div>
            <Btn variant="solid" tone="accent" onClick={() => window.location.reload()}>Reload</Btn>
          </div>
        </Card>
      )}

      {/* status + actions */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            {liveVer ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge tone="good">Live · v{liveVer}</Badge>
                <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Storefront is serving this version.</span>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge tone="warn">Using built-in questions</Badge>
                <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>No published version — the storefront is using the quiz baked into the theme. Publish here to take over.</span>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {msg && <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>{msg}</span>}
            {/* Loads the BUILT-IN starter content, not the live config — the old
                "Load the live quiz" label made it read like the opposite, and
                clicking it on a store that already has content silently replaces
                every field with defaults. */}
            {!doc && <Btn variant="soft" tone="accent" icon="spark" onClick={() => { setDoc(structuredClone(STARTER)); setDirty(true); }}>Load built-in starter content</Btn>}
            {doc && <Btn variant="outline" onClick={saveDraft} disabled={!dirty || !!busy || !!conflict}>{busy === "save" ? "Saving…" : dirty ? "Save draft" : "Saved"}</Btn>}
            {doc && <Btn variant="solid" tone="accent" icon="check" onClick={publish} disabled={!!busy || !!conflict}>{busy === "publish" ? "Publishing…" : "Publish live"}</Btn>}
          </div>
        </div>
      </Card>

      {!doc ? (
        <Card style={{ borderStyle: "dashed" }}>
          <div style={{ padding: 6, fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, lineHeight: 1.6 }}>
            Nothing to edit yet. The quiz currently runs on the copy baked into the Shopify theme.
            Click <b>Load the live quiz</b> to pull in the exact questions, info breaks, scoring and flow that are
            shipping today — then edit them here and publish. From then on the storefront reads its content from
            this dashboard and you can change it without touching the theme.
          </div>
        </Card>
      ) : (
        <QuizBuilder doc={doc} touch={touch} />
      )}
    </div>
  );
}

